/**
 * One company filter, as one object.
 *
 * The register's list page used to hold these 20 fields in 20 separate
 * `useState` hooks. That works fine for a page that only ever sends them to an
 * endpoint, and not at all for the thing about to reuse them: a marketing
 * campaign has to STORE its audience and re-evaluate it at send time, and you
 * cannot store 20 hooks. So the committed filter state lives here, as a plain
 * serialisable object with no React in sight.
 *
 * Two states, deliberately not one:
 *   - `AudienceFilter` is the committed filter. It is what the API receives,
 *     what a campaign saves, and what the chips render from.
 *   - The typing buffers for the debounced fields (search, capital, revenue,
 *     employees) stay in the page as `AudienceDraft`, because "2" on the way to
 *     "20" must not become a saved audience.
 *
 * Server counterpart: apps/api/src/modules/ariregister/lib/audience/entityFilter.ts.
 * The two must agree on field names; the tests there pin what each one selects.
 */
import type { ArListParams } from "./ariregister";

/** Sentinel for a three-state select whose third state is "do not filter".
 *  A real empty string would be indistinguishable from a cleared text input. */
export const ANY = "__any__";

export interface AudienceFilter {
  q: string;
  status: string;
  county: string;
  legalForm: string;
  /** Comma-separated EMTAK code prefixes. MAIN activity only - see the server module. */
  emtak: string;
  hasVat: string;
  hasEmail: boolean;
  hasPhone: boolean;
  /** "true" = the register HAS a website on file. "false" means the company never
   *  told the register about one, which is a lead signal, not proof it has none. */
  hasWebsite: string;
  /** Euros as typed. The server converts to cents. */
  capitalMin: string;
  capitalMax: string;
  revenueMin: string;
  revenueMax: string;
  employeesMin: string;
  employeesMax: string;
  profitable: string;
  /** "true" = on EMTA's newest debtors list, "false" = not on it. "false" is a
   *  weaker claim than it looks: it means "not on that list", which also covers
   *  every company while the arrears import has never run. */
  hasDebt: string;
  /** Arrears floor in euros as typed. Empty means no bound. */
  debtMin: string;
  /** "true" = holds a valid MTR activity licence. Same caveat as `hasDebt`:
   *  "false" means "not in the newest file", not "verified to hold none". */
  hasLicence: string;
  /** ISO instant, snapped to the start of its day - "granted on or after". */
  licenceSince: string | null;
  /** "true" = has won a public procurement (RHR award notices, since
   *  12.05.2023). "false" = no win in the mirror, which also covers every
   *  contract below the procurement threshold - it never reaches the register. */
  wonProcurement: string;
  /** ISO instant, snapped to the start of its day - "won on or after". */
  procurementSince: string | null;
  /** Web need score bounds as typed, 0-100. Empty means no bound. */
  webNeedMin: string;
  webNeedMax: string;
  /** ISO instants, already snapped to the start and end of their day. */
  registeredFrom: string | null;
  registeredTo: string | null;
}

/** The debounced fields' raw typing buffers. Never saved. */
export interface AudienceDraft {
  q: string;
  capitalMin: string;
  capitalMax: string;
  revenueMin: string;
  revenueMax: string;
  employeesMin: string;
  employeesMax: string;
  debtMin: string;
  webNeedMin: string;
  webNeedMax: string;
}

export const EMPTY_AUDIENCE_FILTER: AudienceFilter = {
  q: "",
  status: ANY,
  county: ANY,
  legalForm: ANY,
  emtak: "",
  hasVat: ANY,
  hasEmail: false,
  hasPhone: false,
  hasWebsite: ANY,
  capitalMin: "",
  capitalMax: "",
  revenueMin: "",
  revenueMax: "",
  employeesMin: "",
  employeesMax: "",
  profitable: ANY,
  hasDebt: ANY,
  debtMin: "",
  hasLicence: ANY,
  licenceSince: null,
  wonProcurement: ANY,
  procurementSince: null,
  webNeedMin: "",
  webNeedMax: "",
  registeredFrom: null,
  registeredTo: null,
};

export const EMPTY_AUDIENCE_DRAFT: AudienceDraft = {
  q: "",
  capitalMin: "",
  capitalMax: "",
  revenueMin: "",
  revenueMax: "",
  employeesMin: "",
  employeesMax: "",
  debtMin: "",
  webNeedMin: "",
  webNeedMax: "",
};

/**
 * Merge a partial change, returning the SAME object when nothing actually
 * differs.
 *
 * This is not a micro-optimisation, it is the behaviour the twenty separate
 * hooks used to have for free: `setQ("husly")` when q was already "husly" makes
 * React bail out and nothing refetches. Spreading into a fresh object every
 * time would make the debounce commit a new identity on every tick and refire
 * the list request, so the identity check has to be explicit here instead.
 */
export function applyPatch(f: AudienceFilter, p: Partial<AudienceFilter>): AudienceFilter {
  for (const key of Object.keys(p) as (keyof AudienceFilter)[]) {
    if (f[key] !== p[key]) return { ...f, ...p };
  }
  return f;
}

/** Date → ISO. `endOfDay` pushes the end to 23:59:59 so that the `lte` filter also
 *  catches companies registered on the chosen end day (registeredAt is a DateTime). */
export function toIsoDate(d: Date, endOfDay: boolean): string {
  const x = new Date(d);
  if (endOfDay) x.setHours(23, 59, 59, 999);
  else x.setHours(0, 0, 0, 0);
  return x.toISOString();
}

/**
 * The inverse: a stored instant back to the `YYYY-MM-DD` a day picker shows.
 *
 * ⚠ NOT `iso.slice(0, 10)`, which is what the two licence-date fields used to
 * do. `toIsoDate` stores LOCAL midnight, so east of Greenwich the stored string
 * already reads as the previous day in UTC: pick 10 August in Tallinn and it is
 * saved as `2026-08-09T21:00:00.000Z`. Slicing that hands the field back "9
 * August", so the control disagreed with the chip beside it (which formats the
 * same value with `toLocaleDateString` and got it right) and every reopen of the
 * filter walked the date one day earlier.
 */
export function fromIsoDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * The stored audience: exactly the filter keys and nothing else.
 *
 * Sorting and pagination are deliberately absent. The server schema that
 * receives a saved audience (`registerAudienceSchema`) is `.strict()`, so a
 * stray `page` riding along is a 400 rather than a field somebody ignores - and
 * that is the point: a campaign's audience must not carry "page 3 of the list I
 * happened to be looking at".
 */
export type AudienceParams = Omit<ArListParams, "sort" | "order" | "page" | "pageSize">;

/** Filter → query parameters. `ANY`, empty strings and false become absent,
 *  because "do not filter" and "filter on the empty value" are different asks. */
export function audienceToParams(f: AudienceFilter): AudienceParams {
  return {
    q: f.q || undefined,
    status: f.status === ANY ? undefined : f.status,
    county: f.county === ANY ? undefined : f.county,
    legalForm: f.legalForm === ANY ? undefined : f.legalForm,
    emtak: f.emtak || undefined,
    hasVat: f.hasVat === ANY ? undefined : f.hasVat,
    hasEmail: f.hasEmail ? "true" : undefined,
    hasPhone: f.hasPhone ? "true" : undefined,
    hasWebsite: f.hasWebsite === ANY ? undefined : f.hasWebsite,
    capitalMin: f.capitalMin || undefined,
    capitalMax: f.capitalMax || undefined,
    revenueMin: f.revenueMin || undefined,
    revenueMax: f.revenueMax || undefined,
    employeesMin: f.employeesMin || undefined,
    employeesMax: f.employeesMax || undefined,
    profitable: f.profitable === ANY ? undefined : f.profitable,
    hasDebt: f.hasDebt === ANY ? undefined : f.hasDebt,
    debtMin: f.debtMin || undefined,
    hasLicence: f.hasLicence === ANY ? undefined : f.hasLicence,
    licenceSince: f.licenceSince ?? undefined,
    wonProcurement: f.wonProcurement === ANY ? undefined : f.wonProcurement,
    procurementSince: f.procurementSince ?? undefined,
    webNeedMin: f.webNeedMin || undefined,
    webNeedMax: f.webNeedMax || undefined,
    registeredFrom: f.registeredFrom ?? undefined,
    registeredTo: f.registeredTo ?? undefined,
  };
}

/**
 * Query parameters → filter state. The inverse of `audienceToParams`, for
 * loading a campaign's saved audience back into the form.
 *
 * One asymmetry worth knowing: `hasEmail` and `hasPhone` are switches in the
 * UI, so they can only ever produce "true" or nothing. A hand-written audience
 * carrying `hasEmail: "false"` ("companies with NO address") would come back
 * from this function as off, and saving the form again would drop it. The form
 * cannot express that filter, so it must not pretend to round-trip it.
 */
export function paramsToAudience(p: AudienceParams): AudienceFilter {
  return {
    q: p.q ?? "",
    status: p.status ?? ANY,
    county: p.county ?? ANY,
    legalForm: p.legalForm ?? ANY,
    emtak: p.emtak ?? "",
    hasVat: p.hasVat ?? ANY,
    hasEmail: p.hasEmail === "true",
    hasPhone: p.hasPhone === "true",
    hasWebsite: p.hasWebsite ?? ANY,
    capitalMin: p.capitalMin ?? "",
    capitalMax: p.capitalMax ?? "",
    revenueMin: p.revenueMin ?? "",
    revenueMax: p.revenueMax ?? "",
    employeesMin: p.employeesMin ?? "",
    employeesMax: p.employeesMax ?? "",
    profitable: p.profitable ?? ANY,
    hasDebt: p.hasDebt ?? ANY,
    debtMin: p.debtMin ?? "",
    hasLicence: p.hasLicence ?? ANY,
    licenceSince: p.licenceSince ?? null,
    wonProcurement: p.wonProcurement ?? ANY,
    procurementSince: p.procurementSince ?? null,
    webNeedMin: p.webNeedMin ?? "",
    webNeedMax: p.webNeedMax ?? "",
    registeredFrom: p.registeredFrom ?? null,
    registeredTo: p.registeredTo ?? null,
  };
}

/** The typing buffers seeded from a loaded filter, so the four debounced inputs
 *  show the saved values instead of coming up blank and then clearing them. */
export function draftFromAudience(f: AudienceFilter): AudienceDraft {
  return {
    q: f.q,
    capitalMin: f.capitalMin,
    capitalMax: f.capitalMax,
    revenueMin: f.revenueMin,
    revenueMax: f.revenueMax,
    employeesMin: f.employeesMin,
    employeesMax: f.employeesMax,
    debtMin: f.debtMin,
    webNeedMin: f.webNeedMin,
    webNeedMax: f.webNeedMax,
  };
}

/** Every filter EXCEPT the web need bounds. Split out because the register's
 *  band buttons print a register-wide count on themselves, and that count is
 *  only the truth about what pressing one gives you while nothing else is
 *  narrowing the list - "high 903" next to a list that comes back with 882 is a
 *  button lying about its own outcome. The bounds themselves are excluded so
 *  that pressing a band does not blank the numbers on the band beside it. */
export function hasFilterBesidesWebNeed(f: AudienceFilter): boolean {
  return (
    !!f.q || f.status !== ANY || f.county !== ANY || f.legalForm !== ANY || !!f.emtak ||
    f.hasVat !== ANY || f.hasEmail || f.hasPhone || f.hasWebsite !== ANY ||
    !!f.capitalMin || !!f.capitalMax || !!f.revenueMin || !!f.revenueMax ||
    !!f.employeesMin || !!f.employeesMax || f.profitable !== ANY ||
    f.hasDebt !== ANY || !!f.debtMin ||
    f.hasLicence !== ANY || !!f.licenceSince ||
    f.wonProcurement !== ANY || !!f.procurementSince || !!f.registeredFrom
  );
}

/** True when at least one filter is set. Drives the "clear" button. */
export function hasAnyFilter(f: AudienceFilter): boolean {
  return hasFilterBesidesWebNeed(f) || !!f.webNeedMin || !!f.webNeedMax;
}

/** How many of the collapsed "advanced" filters are set. Shown on the toggle so
 *  a filter hidden behind a fold is never silently applied. */
export function advancedFilterCount(f: AudienceFilter): number {
  return (
    (f.legalForm !== ANY ? 1 : 0) +
    (f.hasVat !== ANY ? 1 : 0) +
    (f.hasEmail ? 1 : 0) +
    (f.hasPhone ? 1 : 0) +
    (f.hasWebsite !== ANY ? 1 : 0) +
    (f.capitalMin || f.capitalMax ? 1 : 0) +
    (f.revenueMin || f.revenueMax ? 1 : 0) +
    (f.employeesMin || f.employeesMax ? 1 : 0) +
    (f.profitable !== ANY ? 1 : 0) +
    (f.hasDebt !== ANY ? 1 : 0) +
    (f.debtMin ? 1 : 0) +
    (f.hasLicence !== ANY ? 1 : 0) +
    (f.licenceSince ? 1 : 0) +
    (f.wonProcurement !== ANY ? 1 : 0) +
    (f.procurementSince ? 1 : 0) +
    (f.webNeedMin || f.webNeedMax ? 1 : 0) +
    (f.registeredFrom ? 1 : 0)
  );
}

/** The stored ISO pair rendered back as the date picker's value. */
export function registeredRange(f: AudienceFilter): { from: Date; to: Date } | null {
  if (!f.registeredFrom || !f.registeredTo) return null;
  return { from: new Date(f.registeredFrom), to: new Date(f.registeredTo) };
}

/**
 * Drop the keys a strict server schema would reject: `undefined` and `null`
 * members, empty strings, empty arrays, and `false` - which means "do not apply
 * this rule" and is not the same as applying it with a negative.
 *
 * ⚠ A zero is KEPT. `scoreMin: 0` is a real bound, not an empty field, and the
 * falsy check that would have dropped it is the obvious way to write this
 * function wrong.
 */
export function pruneFilter<T extends Record<string, unknown>>(filter: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === "" || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out as T;
}

/** The outreach-only keys a campaign audience carries and the register list has
 *  no notion of. Both default to absent, which the server reads as off. */
export interface AudienceExclusions {
  excludeFreemail?: boolean;
  excludeRoleAccounts?: boolean;
}

/**
 * A committed filter, as the audience a campaign stores.
 *
 * ONE FUNCTION, TWO CALLERS: the campaign editor's audience panel and the
 * register list's "make a campaign from this" button. Two copies of this
 * expression would be two ways of turning the same screenful of filters into an
 * audience, and the failure would be silent - a campaign started from the list
 * selecting slightly more or slightly fewer companies than the same filter
 * selected on the page it came from.
 */
export function registerAudience(f: AudienceFilter, exclusions: AudienceExclusions = {}) {
  return pruneFilter({ ...audienceToParams(f), ...exclusions });
}
